import axios, { AxiosResponse } from 'axios';
import { Folder, Document, CreateFolderRequest, UploadDocumentRequest, ApiResponse } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://bajabox-documentation.docsbbx.com/api';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Para usar session authentication
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to handle CSRF token
apiClient.interceptors.request.use((config) => {
  const csrfToken = getCookie('csrftoken');
  if (csrfToken) {
    config.headers['X-CSRFToken'] = csrfToken;
  }
  return config;
});

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect automatically, let components handle 401s
    console.log('API Error:', error.response?.status, error.response?.data);
    return Promise.reject(error);
  }
);

// Helper function to get CSRF cookie
function getCookie(name: string): string | null {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

// API Services
export const folderService = {
  // Get all folders for authenticated user
  getFolders: async (parentFolderId?: number | null): Promise<Folder[]> => {
    const params = parentFolderId ? { parent_folder_id: parentFolderId } : {};
    const response: AxiosResponse<Folder[]> = await apiClient.get('/folders/', { params });
    return response.data;
  },

  // Get ALL folders for authenticated user (without parent filter)
  getAllFolders: async (): Promise<Folder[]> => {
    const response: AxiosResponse<Folder[]> = await apiClient.get('/folders/all/');
    return response.data;
  },

  // Create a new folder
  createFolder: async (data: CreateFolderRequest): Promise<ApiResponse<any>> => {
    const response: AxiosResponse<ApiResponse<any>> = await apiClient.post('/folders/create/', data);
    return response.data;
  },

  // Update a folder
  updateFolder: async (folderId: number, name: string): Promise<ApiResponse<any>> => {
    const response: AxiosResponse<ApiResponse<any>> = await apiClient.put(`/folders/${folderId}/update/`, { name });
    return response.data;
  },

  // Get documents from a specific folder
  getFolderDocuments: async (folderId: number): Promise<Document[]> => {
    const response: AxiosResponse<Document[]> = await apiClient.get(`/folders/${folderId}/documents/`);
    return response.data;
  },

  // Upload document to a folder
  uploadDocument: async (folderId: number, data: UploadDocumentRequest): Promise<ApiResponse<any>> => {
    const formData = new FormData();
    formData.append('file', data.file);
    
    const response: AxiosResponse<ApiResponse<any>> = await apiClient.post(
      `/folders/${folderId}/upload/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  // Delete a document
  deleteDocument: async (documentId: number): Promise<ApiResponse<any>> => {
    const response: AxiosResponse<ApiResponse<any>> = await apiClient.delete(`/documents/${documentId}/delete/`);
    return response.data;
  },

  // Download a document (usando parámetros de consulta)
  downloadDocument: (documentId: number, folderId: number): string => {
    return `${API_BASE_URL}/folders/${folderId}/documents/?download=${documentId}`;
  },

  // View a document (usando parámetros de consulta)
  viewDocument: (documentId: number, folderId: number): string => {
    return `${API_BASE_URL}/folders/${folderId}/documents/?view=${documentId}`;
  },
};

export const authService = {
  // Login user
  login: async (username: string, password: string): Promise<any> => {
    const response = await apiClient.post('/auth/login/', { username, password });
    return response.data;
  },

  // Logout user
  logout: async (): Promise<void> => {
    await apiClient.post('/auth/logout/');
  },

  // Get current user info
  getCurrentUser: async (): Promise<any> => {
    const response = await apiClient.get('/auth/user/');
    return response.data;
  },
};

export const userService = {
  // Get all users (admin only)
  getUsers: async (): Promise<any> => {
    const response = await apiClient.get('/users/');
    return response.data;
  },

  // Create new user (admin only)
  createUser: async (userData: any): Promise<any> => {
    const response = await apiClient.post('/users/', userData);
    return response.data;
  },

  // Update user (admin only)
  updateUser: async (userId: number, userData: any): Promise<any> => {
    const response = await apiClient.put(`/users/${userId}/`, userData);
    return response.data;
  },

  // Delete user (admin only)
  deleteUser: async (userId: number): Promise<any> => {
    const response = await apiClient.delete(`/users/${userId}/`);
    return response.data;
  },

  // Get user details (admin only)
  getUser: async (userId: number): Promise<any> => {
    const response = await apiClient.get(`/users/${userId}/`);
    return response.data;
  },
};

// Export apiClient as api for backward compatibility
export const api = apiClient;
export { apiClient };